import moment from 'moment';
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import Swal from 'sweetalert2';
import { absenceClient } from '../../../../core-module/services/absence/AbsenceClient';
import { AbsencePeriod } from '../../../../core-module/services/absence/types';
import { AbsenceOverviewComponent } from './AbsenceOverviewComponent';
import { EditState } from './types';

interface Props {
  readonly driverId: number;
}

const AbsenceOverviewContainer: React.FC<Props> = ({ driverId }) => {
  const [editState, setEditState] = useState<EditState>({
    editing: false
  });

  const { data: replacersData, isFetching: isFetchingReplacers } = useQuery(
    ['getPossibleReplacers', driverId] as const,
    ({ queryKey }) => {
      const [, driverId] = queryKey;
      return absenceClient.getPossibleReplacers(driverId);
    },
    {
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }
  );

  const { data: absenceData, isFetching: isFetchingAbsence, refetch: refetchAbsence } = useQuery(
    ['getAbsence', driverId] as const,
    ({ queryKey }) => {
      const [, driverId] = queryKey;
      return absenceClient.getAbsence(driverId);
    },
    {
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }
  );

  const {
    data: soonestAllowedAbsenceDateData,
    isFetching: isFetchingSoonestAllowedAbsenceDate
  } = useQuery(['getSoonestAllowedAbsenceDate', driverId] as const, ({ queryKey }) => {
    const [, driverId] = queryKey;
    return absenceClient.getSoonestAllowedAbsenceDate(driverId);
  });

  const handleEditAbsencePeriod = (absencePeriodToEdit?: AbsencePeriod) => {
    setEditState({ editing: true, absencePeriod: absencePeriodToEdit });
  };

  const handleCloseEditing = () => {
    setEditState({ editing: false });
    refetchAbsence();
  };

  const handleCloseReplacementForm = () => {
    refetchAbsence();
  };

  const handleRemoveReplacement = (absencePeriodId: number, replacementId: number) => {
    absenceClient
      .removeReplacement(driverId, absencePeriodId, replacementId)
      .then(() =>
        Swal({
          title: 'Vervanging verwijderd',
          html: 'De vervanging is succesvol verwijderd',
          type: 'success',
          confirmButtonText: 'Ok'
        })
      )
      .catch((err) =>
        Swal({
          title: 'Er is iets misgegaan',
          html: err.message,
          type: 'error',
          confirmButtonText: 'Ok'
        })
      )
      .finally(() => refetchAbsence());
  };

  return (
    <AbsenceOverviewComponent
      driverId={driverId}
      loading={isFetchingAbsence || isFetchingReplacers || isFetchingSoonestAllowedAbsenceDate}
      absencePeriods={absenceData ? absenceData.absencePeriods : []}
      replacers={replacersData ? replacersData.replacers : []}
      soonestAllowedAbsenceDate={
        soonestAllowedAbsenceDateData ? moment(soonestAllowedAbsenceDateData.date) : moment()
      }
      editState={editState}
      onEditAbsencePeriod={handleEditAbsencePeriod}
      onCloseEditing={handleCloseEditing}
      onRemoveReplacement={handleRemoveReplacement}
      onCloseReplacementForm={handleCloseReplacementForm}
    />
  );
};

export { AbsenceOverviewContainer };
